import type { IObservableArray } from 'mobx';

import type { Recommendation, TTDCampaign } from '@feathr/blackbox';
import { useLocalStorageToggle } from '@feathr/hooks';

interface IUseCollapseAndSortProps {
  /** The Tradedesk campaign */
  campaign: TTDCampaign;
  /** The recommendation models */
  recommendations: IObservableArray<Recommendation>;
  /** Whether to show the empty state */
  showEmptyState: boolean;
  /** The recommendation ID param in the URL */
  recommendationIdParam: string | null;
}

interface IUseCollapseAndSortReturnProps {
  /** The displayed recommendations */
  displayedRecommendations: Recommendation[];
  /** Whether to show the collapse button */
  showCollapseButton: boolean;
  /** Function to toggle the collapsed state */
  toggleCollapsed: () => void;
  /** Whether the recommendations are collapsed */
  collapsed: boolean;
}

/**
 * Hook to manage the collapse and sort of the recommendations
 * @param campaign - The Tradedesk campaign
 * @param recommendations - The recommendation models
 * @param showEmptyState - Whether to show the empty state
 * @param recommendationIdParam - The recommendation ID param in the URL
 */
function useCollapseAndSort({
  campaign,
  recommendations,
  showEmptyState,
  recommendationIdParam,
}: IUseCollapseAndSortProps): IUseCollapseAndSortReturnProps {
  const [collapsed, toggleCollapsed] = useLocalStorageToggle({
    initialValue: true,
    namespace: 'recommendations-collapsed',
    key: campaign.id,
  });

  // Sort recommendations to prioritize the matching ID
  const sortedRecommendations = [...recommendations].sort((a, b) => {
    if (a.id === recommendationIdParam) {
      return -1;
    }
    if (b.id === recommendationIdParam) {
      return 1;
    }
    return 0;
  });

  // Collapse recommendations to prioritize the matching ID in the url
  const collapsedRecommendations = collapsed
    ? sortedRecommendations.slice(0, 1)
    : sortedRecommendations;

  // Display no recommendations if empty state is enabled
  const displayedRecommendations = showEmptyState ? [] : collapsedRecommendations;
  const showCollapseButton = recommendations.length > 1;

  return {
    displayedRecommendations,
    showCollapseButton,
    toggleCollapsed,
    collapsed,
  };
}

export default useCollapseAndSort;
